import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS }                from '../typings/chart';
import { ChartPointModel }             from '../typings/intelligence.chart-point';
import { ChartSeriesModel }            from '../typings/intelligence.chart-series';
import { IntelligenceFormatService }   from '../intelligence.format.service';
import { IntelligenceOperationsModel } from '../typings/intelligence.operations';
import { IntelligenceService }         from '../intelligence.service';

@Component({
  selector   : 'wor-intelligence-operations-tab',
  template: require('./intelligence.operations-tab.component.html')
})
export class OperationsTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public conversionValue      : string = '';
  public quoteCloseCaption    : string = '';
  public quoteCloseValue      : string = '';
  public openJobsValue        : string = '';
  public remindersDescription : string = '';
  public turnaroundValue      : string = '';

  public jobsPoints     : Array<ChartPointModel> = [];
  public messagesPoints : Array<ChartPointModel> = [];
  public reminderPoints : Array<ChartPointModel> = [];

  public jobsSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'jobs_completed', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.JOBS_COMPLETED') },
    { color: CHART_COLORS[ 3 ], key: 'bookings_created', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.BOOKINGS_CREATED') },
    { color: CHART_COLORS[ 2 ], key: 'bookings_converted_to_invoice', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.CONVERTED') }
  ];

  public messagesSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 0 ], key: 'sms_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.SMS') },
    { color: CHART_COLORS[ 1 ], key: 'emails_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.EMAIL') },
    { color: CHART_COLORS[ 2 ], key: 'documents_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.DOCUMENTS') }
  ];

  public reminderSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 3 ], key: 'reminders_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.REMINDERS_SENT') },
    { color: CHART_COLORS[ 2 ], key: 'reminder_bookings', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.REMINDERS_BOOKED') }
  ];

  private loadGeneration : number = 0;
  private subscription   : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  dayLabel () : string {
    return this.intelligenceService.previousRange
      ? this.intelligenceFormatService.date(this.intelligenceService.range.start_date)
      : '';
  }

  isJobsEmpty () : boolean {
    return !this.jobsPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isMessagesEmpty () : boolean {
    return !this.messagesPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isRemindersEmpty () : boolean {
    return !this.reminderPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getOperations()
    .then(( data : IntelligenceOperationsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.jobsPoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          bookings_converted_to_invoice: day.bookings_converted_to_invoice,
          bookings_created             : day.bookings_created,
          jobs_completed               : day.jobs_completed
        }
      }));

      this.messagesPoints = ( data.messages || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          documents_sent: day.documents_sent,
          emails_sent   : day.emails_sent,
          sms_sent      : day.sms_sent
        }
      }));

      const remindersSent = data.reminders_sent || 0;
      const reminderBookings = data.reminder_bookings || 0;

      this.reminderPoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          reminder_bookings: day.reminder_bookings,
          reminders_sent   : day.reminders_sent
        }
      }));
      this.remindersDescription = remindersSent
        ? this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.REMINDERS_DESCRIPTION', {
            booked: reminderBookings,
            pct   : Math.round(( reminderBookings / remindersSent ) * 1000) / 10,
            sent  : remindersSent
          })
        : '';

      this.conversionValue = this.intelligenceFormatService.percent(data.booking_conversion_rate);
      this.quoteCloseValue = this.intelligenceFormatService.percent(data.quotes_created
        ? Math.round(( data.quotes_won / data.quotes_created ) * 1000) / 10
        : null);
      this.quoteCloseCaption = data.quotes_created
        ? this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.QUOTE_CLOSE_CAPTION', { won: data.quotes_won, total: data.quotes_created })
        : '';
      this.openJobsValue = this.intelligenceFormatService.count(data.open_jobs_now);
      this.turnaroundValue = this.intelligenceFormatService.hours(this.meanTurnaround(data.series || []));
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  restoreDay () : void {
    this.intelligenceService.restoreRange();
  }

  selectDay ( point : ChartPointModel ) : void {
    this.intelligenceService.selectDay(point.key);
  }

  private meanTurnaround ( series : Array<{ avg_job_turnaround_hours : number }> ) : number {
    const values = series
      .map(day => day.avg_job_turnaround_hours)
      .filter(value => value !== null && value !== undefined);

    return values.length
      ? values.reduce(( sum, value ) => sum + value, 0) / values.length
      : 0;
  }
}
